/**
 * Neuron Report Dispatch — typed API client.
 *
 * One function per route in CONTRACT §7, grouped by resource. Screens should never
 * call fetch() directly: this module owns auth headers, cursor pagination, timeouts,
 * retries and error normalisation, and those need to behave identically everywhere.
 *
 * Every failure arrives as an `ApiError` with a stable `.code`, so a screen can do:
 *
 *   try {
 *     const page = await api.runs.list({ client_id: id, limit: 50 });
 *   } catch (err) {
 *     toast.error(errorMessage(err));          // already human-readable
 *     if (isApiError(err, 'gone')) { ... }     // link expired — contract §1
 *   }
 *
 * Pagination: every list endpoint returns `Page<T> = { items, cursor }`. `cursor: null`
 * means last page. The backend may answer with `next_cursor`; that is normalised here,
 * so screens only ever see `cursor`. Pair with `useCursorPagination` from
 * components/Pagination.tsx.
 */

import { getAccessToken, signOut } from './auth';
import { safeLog } from './format';
import type {
  AccessRow,
  Client,
  ClientCreate,
  ClientPatch,
  DispositionConfig,
  DispositionPatch,
  DownloadCreate,
  DownloadCreateResult,
  DownloadItem,
  DownloadQuery,
  DryRunResult,
  Grant,
  ListParams,
  Message,
  MessageDetail,
  MessageDownloadRequest,
  MessageDownloadResult,
  MessageQuery,
  ObservedFlowsResult,
  Page,
  Recipient,
  RecipientBulkCreate,
  RecipientBulkResult,
  RecipientCreate,
  RecipientPatch,
  Run,
  RunStatus,
  Schedule,
  ScheduleCreate,
  SchedulePatch,
  SesEvent,
  TestMailRequest,
  TestMailResult,
} from './types';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const RAW_BASE = String(import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export const apiConfig = {
  /** No trailing slash. Empty means "same origin", which is what a preview build uses. */
  baseUrl: RAW_BASE,
  defaultTimeoutMs: 30_000,
  /** Generating a report reads a whole object; dry-run and run-now get longer. */
  longTimeoutMs: 120_000,
  defaultPageSize: 50,
};

/**
 * The public download link a recipient receives (contract §1). The token IS the
 * credential, so this URL is never logged and never rendered in full — show
 * `format.maskToken()` and copy the real value to the clipboard.
 */
export function publicDownloadUrl(token: string): string {
  return `${apiConfig.baseUrl}/d/${encodeURIComponent(token)}`;
}

/** Recording resolver (contract §1). Re-mints a fresh 60-second SAS at click time. */
export function publicRecordingUrl(token: string): string {
  return `${apiConfig.baseUrl}/r/${encodeURIComponent(token)}`;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export type ApiErrorCode =
  | 'network'
  | 'timeout'
  | 'aborted'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'gone'
  | 'validation'
  | 'rate_limited'
  | 'server'
  | 'unknown'
  // Anything else is a server-supplied code, passed through verbatim.
  | (string & {});

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly details: unknown;
  readonly requestId: string | undefined;
  readonly retryable: boolean;
  readonly method: string;
  readonly path: string;

  constructor(init: {
    message: string;
    status: number;
    code: ApiErrorCode;
    method: string;
    path: string;
    details?: unknown;
    requestId?: string;
    retryable?: boolean;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
    this.requestId = init.requestId;
    this.method = init.method;
    this.path = init.path;
    this.retryable = init.retryable ?? false;
  }
}

export function isApiError(value: unknown, code?: ApiErrorCode): value is ApiError {
  if (!(value instanceof ApiError)) return false;
  return code ? value.code === code : true;
}

/** A sentence safe to put in a toast. Never contains a token or a raw stack. */
export function errorMessage(value: unknown): string {
  if (value instanceof ApiError) return value.message;
  if (value instanceof Error) return value.message;
  return 'Something went wrong.';
}

const STATUS_CODES: Record<number, ApiErrorCode> = {
  400: 'validation',
  401: 'unauthenticated',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  410: 'gone',
  422: 'validation',
  429: 'rate_limited',
};

const FALLBACK_MESSAGES: Record<ApiErrorCode, string> = {
  network: 'Could not reach the dispatch API. Check your connection and try again.',
  timeout: 'The dispatch API did not respond in time.',
  aborted: 'Request cancelled.',
  unauthenticated: 'Your session has expired. Sign in again.',
  forbidden: 'You do not have access to this.',
  not_found: 'Not found.',
  conflict: 'That conflicts with the current state — reload and try again.',
  gone: 'This link has expired.',
  validation: 'That request was rejected as invalid.',
  rate_limited: 'Too many requests. Try again in a moment.',
  server: 'The dispatch API failed. The run log will have the detail.',
  unknown: 'Something went wrong.',
};

interface ErrorBody {
  message?: string;
  error?: string | { code?: string; message?: string };
  detail?: string | { message?: string };
  code?: string;
  errors?: unknown;
}

function normaliseError(
  status: number,
  body: unknown,
  method: string,
  path: string,
  requestId?: string,
): ApiError {
  const code: ApiErrorCode = STATUS_CODES[status] ?? (status >= 500 ? 'server' : 'unknown');
  let message = '';
  let serverCode: string | undefined;
  let details: unknown = body;

  if (body && typeof body === 'object') {
    const b = body as ErrorBody;
    if (typeof b.message === 'string') message = b.message;
    if (typeof b.error === 'string' && !message) message = b.error;
    if (b.error && typeof b.error === 'object') {
      if (typeof b.error.message === 'string' && !message) message = b.error.message;
      if (typeof b.error.code === 'string') serverCode = b.error.code;
    }
    if (typeof b.detail === 'string' && !message) message = b.detail;
    if (b.detail && typeof b.detail === 'object' && typeof b.detail.message === 'string' && !message) {
      message = b.detail.message;
    }
    if (typeof b.code === 'string') serverCode = serverCode ?? b.code;
    if (b.errors !== undefined) details = b.errors;
  } else if (typeof body === 'string' && body.trim() && body.length < 400) {
    message = body.trim();
  }

  return new ApiError({
    message: message || FALLBACK_MESSAGES[code] || FALLBACK_MESSAGES.unknown,
    status,
    // Prefer the server's own code so screens can branch on domain errors
    // (e.g. `detailed_window_too_wide` from the §3 guardrail).
    code: serverCode ?? code,
    method,
    path,
    details,
    requestId,
    retryable: status === 429 || status >= 500,
  });
}

// ---------------------------------------------------------------------------
// Request plumbing
// ---------------------------------------------------------------------------

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  query?: Record<string, QueryValue>;
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Public routes (/d/, /r/) carry no bearer token — the token in the path is the credential. */
  auth?: boolean;
  /** Retries are for idempotent reads only; POST/PATCH/DELETE never auto-retry. */
  retries?: number;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = `${apiConfig.baseUrl}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, typeof value === 'boolean' ? String(value) : String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return null;
  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text();
  if (!text) return null;
  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}

async function rawRequest<T>(method: string, path: string, options: RequestOptions): Promise<T> {
  const {
    query,
    body,
    signal,
    timeoutMs = apiConfig.defaultTimeoutMs,
    auth = true,
    retries = method === 'GET' ? 2 : 0,
  } = options;

  const url = buildUrl(path, query);
  let attempt = 0;

  // Refreshing the token and retrying a 401 happens at most once per call.
  let reauthorised = false;

  for (;;) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(new DOMException('timeout', 'TimeoutError')), timeoutMs);
    const onOuterAbort = (): void => controller.abort(signal?.reason);
    signal?.addEventListener('abort', onOuterAbort, { once: true });

    try {
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (body !== undefined) headers['Content-Type'] = 'application/json';

      if (auth) {
        const token = await getAccessToken();
        if (!token) {
          throw new ApiError({
            message: FALLBACK_MESSAGES.unauthenticated,
            status: 401,
            code: 'unauthenticated',
            method,
            path,
          });
        }
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
        credentials: 'omit',
        mode: 'cors',
      });

      const requestId =
        response.headers.get('x-amzn-requestid') ??
        response.headers.get('apigw-requestid') ??
        response.headers.get('x-request-id') ??
        undefined;

      if (response.ok) return (await parseBody(response)) as T;

      // 401: the token may simply have aged out mid-flight. Refresh once, retry once,
      // and only then hand the operator back to the hosted UI.
      if (response.status === 401 && auth && !reauthorised) {
        reauthorised = true;
        const { refreshSession } = await import('./auth');
        const refreshed = await refreshSession();
        if (refreshed) continue;
        safeLog('api', 'session could not be refreshed; signing out');
        signOut({ local: true });
      }

      const error = normaliseError(response.status, await parseBody(response), method, path, requestId);

      if (error.retryable && attempt < retries) {
        const retryAfter = Number(response.headers.get('retry-after'));
        const backoff = Number.isFinite(retryAfter) && retryAfter > 0
          ? retryAfter * 1000
          : 300 * 2 ** attempt + Math.random() * 200;
        attempt += 1;
        await sleep(backoff);
        continue;
      }

      throw error;
    } catch (err) {
      if (err instanceof ApiError) throw err;

      // abort(reason) makes fetch reject with the reason object itself, so our timeout
      // arrives as TimeoutError and a caller cancellation as AbortError. Matching only
      // AbortError would report every timeout as an unreachable API.
      if (
        err instanceof DOMException &&
        (err.name === 'AbortError' || err.name === 'TimeoutError')
      ) {
        if (signal?.aborted) {
          throw new ApiError({
            message: FALLBACK_MESSAGES.aborted,
            status: 0,
            code: 'aborted',
            method,
            path,
          });
        }
        if (attempt < retries) {
          attempt += 1;
          await sleep(300 * 2 ** attempt);
          continue;
        }
        throw new ApiError({
          message: FALLBACK_MESSAGES.timeout,
          status: 0,
          code: 'timeout',
          method,
          path,
          retryable: true,
        });
      }

      if (attempt < retries) {
        attempt += 1;
        await sleep(300 * 2 ** attempt + Math.random() * 200);
        continue;
      }

      throw new ApiError({
        message: FALLBACK_MESSAGES.network,
        status: 0,
        code: 'network',
        method,
        path,
        details: err instanceof Error ? err.message : String(err),
        retryable: true,
      });
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onOuterAbort);
    }
  }
}

export const http = {
  get: <T>(path: string, options: RequestOptions = {}) => rawRequest<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    rawRequest<T>('POST', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options: RequestOptions = {}) =>
    rawRequest<T>('PATCH', path, { ...options, body }),
  del: <T>(path: string, options: RequestOptions = {}) => rawRequest<T>('DELETE', path, options),
};

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

interface RawPage<T> {
  items?: T[];
  results?: T[];
  cursor?: string | null;
  next_cursor?: string | null;
  total?: number;
  truncated?: boolean;
}

/** Accept `{items,cursor}`, `{items,next_cursor}`, `{results,...}` or a bare array. */
function toPage<T>(raw: unknown): Page<T> {
  if (Array.isArray(raw)) return { items: raw as T[], cursor: null };
  if (!raw || typeof raw !== 'object') return { items: [], cursor: null };
  const page = raw as RawPage<T>;
  return {
    items: page.items ?? page.results ?? [],
    cursor: page.next_cursor ?? page.cursor ?? null,
    // Carry the server's own count through. Dropping it made RunDetail present the page
    // length as the total — "50 links issued" on a run that issued 575.
    total: typeof page.total === 'number' ? page.total : undefined,
    truncated: typeof page.truncated === 'boolean' ? page.truncated : undefined,
  };
}

async function getPage<T>(path: string, options: RequestOptions = {}): Promise<Page<T>> {
  return toPage<T>(await rawRequest<unknown>('GET', path, options));
}

/**
 * Walk every page of a list endpoint. Use for exports and pickers that genuinely need
 * the full set (the client dropdown); do NOT use it to render a table — that is what
 * cursor pagination is for. `maxPages` is a seatbelt against a cursor that never ends.
 */
export async function fetchAllPages<T>(
  fetchPage: (cursor?: string) => Promise<Page<T>>,
  { maxPages = 50 }: { maxPages?: number } = {},
): Promise<T[]> {
  const all: T[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < maxPages; i += 1) {
    const page = await fetchPage(cursor);
    all.push(...page.items);
    if (!page.cursor) return all;
    cursor = page.cursor;
  }
  safeLog('api', `pagination stopped at the ${maxPages}-page seatbelt`, { returned: all.length });
  return all;
}

function listParams(params: ListParams = {}): Record<string, QueryValue> {
  return { cursor: params.cursor, limit: params.limit ?? apiConfig.defaultPageSize };
}

// ---------------------------------------------------------------------------
// Endpoints — CONTRACT §7
// ---------------------------------------------------------------------------

const clients = {
  /** GET /api/clients */
  list: (params: ListParams & { active?: boolean } = {}, options: RequestOptions = {}) =>
    getPage<Client>('/api/clients', {
      ...options,
      query: { ...listParams(params), active: params.active },
    }),

  /** Every client, for pickers. Small, bounded set (3 seeded). */
  listAll: (options: RequestOptions = {}) =>
    fetchAllPages<Client>((cursor) =>
      getPage<Client>('/api/clients', { ...options, query: { cursor, limit: 100 } }),
    ),

  /** GET /api/clients/{id} */
  get: (clientId: string, options: RequestOptions = {}) =>
    http.get<Client>(`/api/clients/${encodeURIComponent(clientId)}`, options),

  /**
   * POST /api/clients/{id}/flows/refresh — read the client's recent exports and store the
   * campaign names they contain, so the schedule editor can offer them instead of asking
   * an operator to type one from memory. Samples; a scheduled run counts every row and
   * its numbers replace these.
   */
  refreshFlows: (clientId: string, options: RequestOptions = {}) =>
    http.post<ObservedFlowsResult>(
      `/api/clients/${encodeURIComponent(clientId)}/flows/refresh`,
      {},
      options,
    ),

  /** POST /api/clients */
  create: (body: ClientCreate, options: RequestOptions = {}) =>
    http.post<Client>('/api/clients', body, options),

  /** PATCH /api/clients/{id} */
  patch: (clientId: string, body: ClientPatch, options: RequestOptions = {}) =>
    http.patch<Client>(`/api/clients/${encodeURIComponent(clientId)}`, body, options),
};

const recipients = {
  /** GET /api/clients/{id}/recipients */
  list: (clientId: string, params: ListParams = {}, options: RequestOptions = {}) =>
    getPage<Recipient>(`/api/clients/${encodeURIComponent(clientId)}/recipients`, {
      ...options,
      query: listParams(params),
    }),

  /** POST /api/clients/{id}/recipients */
  create: (clientId: string, body: RecipientCreate, options: RequestOptions = {}) =>
    http.post<Recipient>(`/api/clients/${encodeURIComponent(clientId)}/recipients`, body, options),

  /**
   * POST /api/clients/{id}/recipients/bulk — CONTRACT-RECIPIENTS §2.
   *
   * Many addresses at once, validated as a whole before anything is written: if one
   * entry is malformed the request is refused with `invalid_email_in_list` and nothing
   * lands, because half a list written is worse than none — nobody can tell which half.
   *
   * A duplicate is NOT an error here, unlike `create`'s 409 `recipient_exists`. One
   * address that already exists is a mistake worth stopping; the same address inside a
   * list of twelve is just the list being complete.
   */
  createBulk: (clientId: string, body: RecipientBulkCreate, options: RequestOptions = {}) =>
    http.post<RecipientBulkResult>(
      `/api/clients/${encodeURIComponent(clientId)}/recipients/bulk`,
      body,
      options,
    ),

  /** PATCH /api/recipients/{rid} */
  patch: (recipientId: string, body: RecipientPatch, options: RequestOptions = {}) =>
    http.patch<Recipient>(`/api/recipients/${encodeURIComponent(recipientId)}`, body, options),

  /** DELETE /api/recipients/{rid} */
  remove: (recipientId: string, options: RequestOptions = {}) =>
    http.del<void>(`/api/recipients/${encodeURIComponent(recipientId)}`, options),
};

const schedules = {
  /** GET /api/schedules */
  list: (
    params: ListParams & { client_id?: string; enabled?: boolean } = {},
    options: RequestOptions = {},
  ) =>
    getPage<Schedule>('/api/schedules', {
      ...options,
      query: { ...listParams(params), client_id: params.client_id, enabled: params.enabled },
    }),

  /** POST /api/schedules */
  create: (body: ScheduleCreate, options: RequestOptions = {}) =>
    http.post<Schedule>('/api/schedules', body, options),

  /** PATCH /api/schedules/{sid} */
  patch: (scheduleId: string, body: SchedulePatch, options: RequestOptions = {}) =>
    http.patch<Schedule>(`/api/schedules/${encodeURIComponent(scheduleId)}`, body, options),

  /** DELETE /api/schedules/{sid} */
  remove: (scheduleId: string, options: RequestOptions = {}) =>
    http.del<void>(`/api/schedules/${encodeURIComponent(scheduleId)}`, options),

  /**
   * POST /api/schedules/{sid}/dry-run — contract §8.4.
   * Resolves the source key, recipients and the exact column list without sending.
   * Slow by design (it reads the object), so it gets the long timeout.
   */
  dryRun: (scheduleId: string, options: RequestOptions = {}) =>
    http.post<DryRunResult>(`/api/schedules/${encodeURIComponent(scheduleId)}/dry-run`, undefined, {
      timeoutMs: apiConfig.longTimeoutMs,
      ...options,
    }),

  /** POST /api/schedules/{sid}/run-now — fires a real dispatch; returns the run to follow. */
  runNow: (scheduleId: string, options: RequestOptions = {}) =>
    http.post<{ run_id: string; status: RunStatus }>(
      `/api/schedules/${encodeURIComponent(scheduleId)}/run-now`,
      undefined,
      { timeoutMs: apiConfig.longTimeoutMs, ...options },
    ),

  /**
   * POST /api/schedules/{sid}/test-mail — CONTRACT-RECIPIENTS §3.
   *
   * Sends the REAL rendered report to one internal address, so an operator can see what
   * a client will get before enabling a schedule. Everything but the destination comes
   * from the schedule row: a test assembled from hand-passed parameters tests an ask
   * that will never fire.
   *
   * 202, not 200 — the dispatcher invoke is asynchronous, so nothing has been rendered
   * or sent when this resolves. Poll `runs.get()` exactly as run-now does.
   */
  testMail: (scheduleId: string, body: TestMailRequest = {}, options: RequestOptions = {}) =>
    http.post<TestMailResult>(
      `/api/schedules/${encodeURIComponent(scheduleId)}/test-mail`,
      body,
      options,
    ),
};

const runs = {
  /**
   * GET /api/runs?client_id=&limit=
   *
   * Test sends are excluded unless `include_test` is set, and the exclusion is the
   * server's (CONTRACT-RECIPIENTS §5) — every tile on the dashboard counts runs through
   * this call, and an operator's test must never appear in "runs today" or in a client's
   * delivery figures.
   */
  list: (
    params: ListParams & { client_id?: string; status?: RunStatus; include_test?: boolean } = {},
    options: RequestOptions = {},
  ) =>
    getPage<Run>('/api/runs', {
      ...options,
      query: {
        ...listParams(params),
        client_id: params.client_id,
        status: params.status,
        include_test: params.include_test ? true : undefined,
      },
    }),

  /** GET /api/runs/{run_id} */
  get: (runId: string, options: RequestOptions = {}) =>
    http.get<Run>(`/api/runs/${encodeURIComponent(runId)}`, options),

  /**
   * POST /api/runs/{run_id}/resend — re-sends the existing artifact.
   * Mints NEW grants (contract §1: re-issue never extends the old token).
   */
  resend: (
    runId: string,
    body: { recipient_ids?: string[] } = {},
    options: RequestOptions = {},
  ) =>
    http.post<{ run_id: string; status: RunStatus; ses_message_ids: string[] }>(
      `/api/runs/${encodeURIComponent(runId)}/resend`,
      body,
      { timeoutMs: apiConfig.longTimeoutMs, ...options },
    ),

  /** GET /api/runs/{run_id}/grants */
  grants: (runId: string, params: ListParams = {}, options: RequestOptions = {}) =>
    getPage<Grant>(`/api/runs/${encodeURIComponent(runId)}/grants`, {
      ...options,
      query: listParams(params),
    }),

  /** GET /api/runs/{run_id}/access — the audit trail behind those grants (nrd-access). */
  access: (runId: string, params: ListParams = {}, options: RequestOptions = {}) =>
    getPage<AccessRow>(`/api/runs/${encodeURIComponent(runId)}/access`, {
      ...options,
      query: listParams(params),
    }),

  /** GET /api/runs/{run_id}/ses-events — delivery, bounce and complaint history. */
  sesEvents: (runId: string, params: ListParams = {}, options: RequestOptions = {}) =>
    getPage<SesEvent>(`/api/runs/${encodeURIComponent(runId)}/ses-events`, {
      ...options,
      query: listParams(params),
    }),
};

const grants = {
  /**
   * POST /api/grants/{token_hash}/revoke — kills a link immediately.
   * The resolver checks `revoked_at` before expiry, so this takes effect on the very
   * next click with no propagation delay.
   */
  revoke: (tokenHash: string, body: { reason?: string } = {}, options: RequestOptions = {}) =>
    http.post<Grant>(`/api/grants/${encodeURIComponent(tokenHash)}/revoke`, body, options),
};

const downloads = {
  /** GET /api/downloads?client_id=&from=&to=&shape=&audio_only= */
  list: (params: DownloadQuery = {}, options: RequestOptions = {}) =>
    getPage<DownloadItem>('/api/downloads', {
      ...options,
      query: {
        client_id: params.client_id,
        from: params.from,
        to: params.to,
        shape: params.shape,
        audio_only: params.audio_only,
        cursor: params.cursor,
        limit: params.limit ?? apiConfig.defaultPageSize,
      },
    }),

  /**
   * POST /api/downloads — generate on demand. Returns a run_id; poll runs.get() until
   * status leaves RUN_IN_FLIGHT.
   */
  create: (body: DownloadCreate, options: RequestOptions = {}) =>
    http.post<DownloadCreateResult>('/api/downloads', body, {
      timeoutMs: apiConfig.longTimeoutMs,
      ...options,
    }),
};

/**
 * Sent mail archive — CONTRACT-MAIL §4.
 *
 * The run record keeps the subject and the SES message ids; it does not keep the
 * message, and SES retains nothing once a send is accepted. These read the copy the
 * dispatcher archived at send time, which is the only thing that can answer "what did
 * they actually receive?" — re-rendering the template today would answer a different
 * question.
 */
const messages = {
  /** GET /api/messages?client_id=&status=&from=&to=&cursor=&limit= — newest first, no body. */
  list: (params: MessageQuery = {}, options: RequestOptions = {}) =>
    getPage<Message>('/api/messages', {
      ...options,
      query: {
        client_id: params.client_id,
        status: params.status,
        from: params.from,
        to: params.to,
        cursor: params.cursor,
        limit: params.limit ?? apiConfig.defaultPageSize,
        // Absent, not `false`: the server's filter is
        // `attribute_not_exists(is_test) OR is_test = false`, and every row written
        // before test mail shipped carries no such attribute at all.
        include_test: params.include_test ? true : undefined,
      },
    }),

  /**
   * GET /api/messages/{message_id} — the row plus `{ html, text }` read from S3.
   * A 200 with `html: null` and a `body_missing` reason means the row is fine and the
   * archived object is not; that is a different situation from a 404 and reads as one.
   */
  get: (messageId: string, options: RequestOptions = {}) =>
    http.get<MessageDetail>(`/api/messages/${encodeURIComponent(messageId)}`, options),

  /**
   * POST /api/messages/{message_id}/download — mints a short-lived OPERATOR grant for
   * one artifact the mail offered and returns its URL.
   *
   * It never reuses or exposes the recipient's token: the console holds only token
   * hashes by design, and an operator opening a file is a different audit event from
   * the client opening their link. Conflating the two corrupts the trail.
   */
  download: (
    messageId: string,
    body: MessageDownloadRequest,
    options: RequestOptions = {},
  ) =>
    http.post<MessageDownloadResult>(
      `/api/messages/${encodeURIComponent(messageId)}/download`,
      body,
      options,
    ),
};

const dispositions = {
  /** GET /api/dispositions/{client_id} */
  get: (clientId: string, options: RequestOptions = {}) =>
    http.get<DispositionConfig>(`/api/dispositions/${encodeURIComponent(clientId)}`, options),

  /**
   * PATCH /api/dispositions/{client_id}
   *
   * CONTRACT §5: values are stored and displayed EXACTLY as written. Do not trim case,
   * expand abbreviations or correct spelling on the way in — `MAIN_MESSAGE` and
   * `Main_Message` are the same outcome to the matcher but two different strings to us.
   */
  patch: (clientId: string, body: DispositionPatch, options: RequestOptions = {}) =>
    http.patch<DispositionConfig>(`/api/dispositions/${encodeURIComponent(clientId)}`, body, options),
};

export const api = {
  clients,
  recipients,
  schedules,
  runs,
  grants,
  downloads,
  messages,
  dispositions,
};

export type Api = typeof api;
